// Data transformers for StockWaves

export interface CSVWeeklyData {
  Week_Offset: number;
  Expiry_Date: string;
  Bullish_Percent: number;
  Total_Net_Delta: number;
  Total_Dollar_Delta: number;
  Market_Signal: string;
}

export interface WaveDataPoint {
  week: string;
  date: string;
  sentiment: number;
  isBullish: boolean;
  strength: number;
  bullishPercent?: number;
  dollarDelta?: number;
  netDelta?: number;
  isAnomaly?: boolean;
}

export function transformCSVToWaveData(csvData: CSVWeeklyData[]): WaveDataPoint[] {
  // Sort by week offset to ensure correct order
  const sortedData = [...csvData].sort((a, b) => a.Week_Offset - b.Week_Offset);
  
  // Calculate average delta to identify anomalies
  const avgDelta = sortedData.reduce((sum, d) => sum + Math.abs(d.Total_Net_Delta), 0) / sortedData.length;
  
  return sortedData.map(row => {
    // Convert bullish percentage to sentiment score (-1 to 1)
    // But amplify it based on delta magnitude
    const baseSentiment = (row.Bullish_Percent - 50) / 50;
    
    // Calculate delta magnitude relative to average
    const deltaMagnitude = Math.abs(row.Total_Net_Delta) / avgDelta;
    
    // Amplify sentiment for high delta weeks (but cap at reasonable levels)
    const amplificationFactor = Math.min(Math.sqrt(deltaMagnitude), 1.5);
    const rawSentiment = baseSentiment * amplificationFactor;
    // Clamp sentiment to -1 to 1 range
    const sentiment = Math.max(-1, Math.min(1, rawSentiment));
    
    // Calculate strength based on both percentage deviation AND delta magnitude
    const percentStrength = Math.abs(baseSentiment);
    const deltaStrength = Math.min(deltaMagnitude / 3, 1); // Normalize to 0-1
    const strength = (percentStrength + deltaStrength) / 2;
    
    // Format date for display - parse as local date to avoid timezone issues
    const [year, month, day] = row.Expiry_Date.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    const formattedDate = date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
    
    return {
      week: `Week ${row.Week_Offset}`,
      date: formattedDate,
      sentiment: sentiment,
      isBullish: sentiment > 0, // Use sentiment value to determine color, not Market_Signal
      strength: strength,
      bullishPercent: row.Bullish_Percent,
      dollarDelta: row.Total_Dollar_Delta,
      netDelta: row.Total_Net_Delta,
      isAnomaly: deltaMagnitude > 2 // Flag weeks with 2x+ average delta
    } as WaveDataPoint;
  });
}

export function getMarketTrend(waveData: WaveDataPoint[]): string {
  if (waveData.length < 2) return 'neutral';
  
  // Compare first half average to second half average
  const midpoint = Math.floor(waveData.length / 2);
  const firstHalf = waveData.slice(0, midpoint);
  const secondHalf = waveData.slice(midpoint);
  
  const avgFirst = firstHalf.reduce((sum, d) => sum + d.sentiment, 0) / firstHalf.length;
  const avgSecond = secondHalf.reduce((sum, d) => sum + d.sentiment, 0) / secondHalf.length;
  
  if (avgSecond > avgFirst + 0.1) return 'increasingly bullish';
  if (avgSecond < avgFirst - 0.1) return 'increasingly bearish';
  return 'stable';
}

export function findInflectionPoints(waveData: WaveDataPoint[]): number[] {
  const inflectionWeeks: number[] = [];
  
  for (let i = 1; i < waveData.length - 1; i++) {
    const prev = waveData[i - 1].sentiment;
    const current = waveData[i].sentiment;
    const next = waveData[i + 1].sentiment;
    
    // Check for local maximum or minimum
    if ((current > prev && current > next) || (current < prev && current < next)) {
      inflectionWeeks.push(i);
    }
    
    // Check for trend reversal
    if ((prev < 0 && next > 0) || (prev > 0 && next < 0)) {
      inflectionWeeks.push(i);
    }
  }
  
  return inflectionWeeks;
}